import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, AlertTriangle, CreditCard, Scale } from 'lucide-react';

const CGVPage = () => {
  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">Conditions Générales de Vente et d'Utilisation</h1>
        
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-primary" />
              CGVU – NautiCoach
            </CardTitle>
            <p className="text-sm text-muted-foreground">Dernière mise à jour : 26 mars 2026</p>
          </CardHeader>
          <CardContent className="space-y-8">

            {/* Article 1 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 1 – Objet et Définitions</h2>
              <p className="text-muted-foreground mb-2">
                Les présentes CGVU régissent l'accès et l'utilisation de NautiCoach, plateforme SaaS (Software as a Service) de gestion nautique.
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Service :</strong> L'application, l'assistant IA, les outils d'inventaire et de maintenance.</li>
                <li><strong>Utilisateur :</strong> Toute personne physique ou morale inscrite sur la plateforme.</li>
              </ul>
            </section>

            {/* Article 2 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 2 – Acceptation et Modification</h2>
              <p className="text-muted-foreground">
                L'inscription au Service implique l'acceptation sans réserve des présentes CGVU. NautiCoach se réserve le droit de modifier ces conditions à tout moment. L'Utilisateur sera informé par notification in-app ou e-mail <strong>30 jours</strong> avant l'entrée en vigueur des nouvelles conditions.
              </p>
            </section>

            {/* Article 3 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 3 – Description des Services</h2>
              <p className="text-muted-foreground mb-2">
                NautiCoach fournit des outils d'aide à la gestion de navires :
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Inventaire & Stock :</strong> Suivi numérique du matériel embarqué.</li>
                <li><strong>Maintenance Préventive :</strong> Alertes basées sur les données saisies par l'Utilisateur.</li>
                <li><strong>Checklists :</strong> Listes de vérification pour la navigation.</li>
                <li><strong>Assistant IA :</strong> Support conversationnel basé sur le traitement de données textuelles.</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3 bg-muted/50 p-3 rounded-lg">
                ⚠️ <strong>Note :</strong> L'Assistant IA fournit des réponses informatives et ne constitue pas une expertise maritime certifiée.
              </p>
            </section>

            {/* Article 4 */}
            <section className="border border-destructive/30 rounded-lg p-5">
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Article 4 – Responsabilité et Sécurité
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="font-semibold text-foreground">4.1 Statut de Chef de Bord</h3>
                  <p>
                    L'Utilisateur reconnaît qu'il demeure seul et unique "Chef de Bord" responsable de la sécurité de son navire et de son équipage. NautiCoach est un <strong>outil de confort</strong> et non un instrument de navigation homologué.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">4.2 Limitations de l'IA</h3>
                  <p>
                    Les conseils de l'IA sont basés sur des probabilités statistiques. L'Utilisateur doit <strong>impérativement vérifier physiquement</strong> l'état de son navire (vannes, moteur, gréement) avant toute prise de mer.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">4.3 Exclusions</h3>
                  <p>
                    NautiCoach ne saurait être tenu responsable des dommages directs ou indirects (naufrage, avarie, remorquage, perte de jouissance) résultant d'une erreur de l'IA, d'une panne du service ou d'une mauvaise saisie de l'Utilisateur.
                  </p>
                </div>
              </div>
            </section>

            {/* Article 5 */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                Article 5 – Tarifs et Paiement
              </h2>
              <p className="text-muted-foreground mb-2">
                Les services Premium sont facturés selon les tarifs en vigueur indiqués en Euros TTC.
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Abonnement :</strong> Le paiement est récurrent (mensuel ou annuel).</li>
                <li><strong>Évolution :</strong> NautiCoach peut modifier ses tarifs après information préalable de l'Utilisateur (préavis de 30 jours).</li>
                <li><strong>Rétractation :</strong> Conformément au Code de la consommation, l'Utilisateur accepte que l'exécution du service commence immédiatement après paiement et renonce expressément à son droit de rétractation pour le contenu numérique non fourni sur support matériel.</li>
              </ul>
            </section>

            {/* Article 6 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 6 – Obligations de l'Utilisateur</h2>
              <p className="text-muted-foreground mb-2">L'Utilisateur s'engage à :</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li>Fournir des données exactes (Immatriculation, HIN, dates de maintenance).</li>
                <li>Maintenir la confidentialité de ses identifiants.</li>
                <li>Ne pas tenter d'extraire, copier ou "scrapper" les algorithmes ou la base de données de NautiCoach.</li>
              </ul>
            </section>

            {/* Article 7 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 7 – Propriété Intellectuelle</h2>
              <p className="text-muted-foreground">
                L'ensemble des éléments de NautiCoach (code source, structure de la base de données, design, logos, "System Prompts" de l'IA) est la propriété exclusive de l'Éditeur. Toute reproduction ou ingénierie inverse est <strong>strictement interdite</strong> et passible de poursuites.
              </p>
            </section>

            {/* Article 8 */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Article 8 – Données Personnelles (RGPD)
              </h2>
              <p className="text-muted-foreground">
                Le traitement des données est régi par notre <a href="/politique-confidentialite" className="text-primary underline">Politique de Confidentialité</a>. Les données techniques du navire (HIN, MMSI) sont traitées avec le même niveau de sécurité que les données personnelles.
              </p>
            </section>

            {/* Article 9 */}
            <section>
              <h2 className="text-xl font-semibold mb-3">Article 9 – Résiliation</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Par l'Utilisateur :</strong> À tout moment via l'espace personnel. L'accès reste valide jusqu'à la fin de la période payée.</li>
                <li><strong>Par NautiCoach :</strong> En cas de non-respect des CGVU ou défaut de paiement, après mise en demeure restée infructueuse.</li>
              </ul>
            </section>

            {/* Article 10 */}
            <section className="bg-muted/30 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">Article 10 – Droit Applicable et Litiges</h2>
              <p className="text-muted-foreground">
                Les présentes conditions sont soumises au <strong>Droit Français</strong>. En cas de litige n'ayant pu faire l'objet d'un accord amiable, compétence exclusive est attribuée aux tribunaux du ressort du siège social de l'Éditeur.
              </p>
            </section>

          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CGVPage;
